import {
  Center,
  Flex,
  Grid,
  Link,
  Text,
  Tooltip,
  useColorModeValue,
  FlexProps,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useSideBarContext } from '../hooks/sideBarContext';
import { Icon } from 'packages/common-frontend/components/icons';
import { isAdminDashboard } from 'packages/common-frontend/utils';

function compareAdminPaths(currentPath: string, routeName: string) {
  if (currentPath) {
    const endOfRoute = routeName.substring(routeName.lastIndexOf('/') + 1);
    return (
      _.includes(routeName, currentPath) ||
      (endOfRoute && _.startsWith(currentPath, endOfRoute))
    );
  }
  // not sure why but for `organizations` the currentPath is '', only discernable thing is it ends with `/`
  return _.endsWith(routeName, '/');
}

/**
 * @param routeName the base name of the route which is used for matching the currentPath to determine whether a sidebar item should be highlighted. eg. /settings/faclities/borrowing-base
 * @param routeAddress the link address that clicking sidebar item should navigate to. Only include if this is different than the route name. It will be necessary to include this routeAddress param when we want to navigate to an address that includes query params. eg. /settings/facilities/borrowing-base?creditFacility="someId"
 * @returns a single item for the sidebar
 */
export const SideBarItem = ({
  currentPath,
  routeName,
  routeAddress,
  strictPathMatch,
  label,
  icon,
  iconOnly,
  textViewMarginLeft,
  onClick,
}: {
  currentPath: string;
  routeName: string;
  routeAddress?: string;
  strictPathMatch?: boolean;
  label: string;
  icon: string;
  iconOnly?: boolean;
  textViewMarginLeft?: string;
  onClick?: () => void;
}) => {
  const { toggleSideBar } = useSideBarContext();

  const selectedColor = useColorModeValue('gray.300', 'gray.100');

  // compare againt currentPath without leading /
  const currentPathMatched =
    routeName === '' || strictPathMatch
      ? currentPath === `/${routeName}`
      : _.startsWith(currentPath, `/${routeName}`) ||
        // admin dash url matching works differently
        (isAdminDashboard() && compareAdminPaths(currentPath, routeName));

  const sharedFlexProps: FlexProps = {
    borderRadius: '4px',
    borderColor: 'black',
    color: currentPathMatched ? 'black' : 'inherit',
    borderWidth: currentPathMatched ? '1px' : '0px',
    bgColor: currentPathMatched ? selectedColor : 'inherit',
    h: '9',
  };

  const toolTipBgColor = useColorModeValue('black', 'white');
  const toolTipColor = useColorModeValue('white', 'black');

  return (
    <Link
      as={RouterLink}
      to={`/${routeAddress ?? routeName}`}
      style={{ padding: '2px 0', marginTop: '0' }}
      _focus={{ boxShadow: 'none' }}
      onClick={() => {
        Sentry.addBreadcrumb({
          category: 'navigation',
          message: `Clicked navigation to ${routeName || 'home'}`,
          level: Sentry.Severity.Info,
        });
        toggleSideBar();

        onClick?.();
      }}
    >
      {iconOnly ? (
        <Tooltip
          label={label}
          placement="right"
          gutter={12}
          bgColor={toolTipBgColor}
          color={toolTipColor}
          borderRadius="4px"
          px="8px"
          py="4px"
        >
          <Flex
            w="45px"
            justifyContent="center"
            _hover={{ bg: selectedColor, color: 'black' }}
            {...sharedFlexProps}
          >
            <Center>
              <Icon icon={icon} size="medium" />
            </Center>
          </Flex>
        </Tooltip>
      ) : (
        <Flex
          ml={textViewMarginLeft ?? '0px'}
          pl="10px"
          alignItems="center"
          _hover={{ bg: selectedColor, color: 'black' }}
          {...sharedFlexProps}
        >
          <Grid mr="16px" height={8} placeItems="center">
            <Icon icon={icon} size="medium" />
          </Grid>
          <Text
            textShadow={currentPathMatched ? '0px 0px 1px' : 'none'}
            className="side-bar-item"
            fontSize="14px"
          >
            {label}
          </Text>
        </Flex>
      )}
    </Link>
  );
};
